import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/getErrorMessage';

/**
 * Runs one case command. The request id is minted once per open form, so a
 * retry after a network failure replays the same command instead of applying
 * it twice. Server refusals stay in the form.
 */
export function useCaseCommand<TInput, TResult>(
  companyId: string,
  caseId: string | null,
  run: (requestId: string, input: TInput) => Promise<TResult>,
  successMessage: string,
  onDone: (result: TResult) => void
): { submit: (input: TInput) => void; isPending: boolean; error: string | null } {
  const [requestId] = useState(() => crypto.randomUUID());
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: TInput) => run(requestId, input),
    onSuccess: (result) => {
      toast.success(successMessage);
      void queryClient.invalidateQueries({ queryKey: ['hr-cases'] });
      if (caseId) {
        void queryClient.invalidateQueries({ queryKey: ['hr-case', companyId, caseId] });
      }
      onDone(result);
    },
    onError: (mutationError) => {
      const message = getErrorMessage(mutationError);
      setError(message);
      toast.error(message);
    },
  });
  return {
    submit: (input) => {
      setError(null);
      mutation.mutate(input);
    },
    isPending: mutation.isPending,
    error,
  };
}
